import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { dbSelect, dbUpdate, dbInsert } from '../../lib/supabase';
import { blogSeedData } from '../../data/blogSeedData';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [viewsUpdated, setViewsUpdated] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentName, setCommentName] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentSubmitted, setCommentSubmitted] = useState(false);

  const articleRef = useRef(null);

  // Track page scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        setScrollProgress((window.scrollY / totalHeight) * 100);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch current blog and other articles
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const { data } = await dbSelect('blogs', 'publishedDate', false);
        const allPosts = (data && data.length > 0) ? data : blogSeedData;
        setBlogs(allPosts);

        const currentPost = allPosts.find(b => b.slug === slug);
        if (currentPost) {
          setPost(currentPost);

          // Fetch comments for this post
          const { data: comms } = await dbSelect('blog_comments', 'createdAt', true);
          if (comms) {
            setComments(comms.filter(c => c.postSlug === slug && c.status === 'approved'));
          }

          // Increment view count exactly once per slug mount
          if (!viewsUpdated) {
            const updatedViews = (currentPost.views || 0) + 1;
            await dbUpdate('blogs', currentPost.id, { views: updatedViews });
            setViewsUpdated(true);
          }
        }
      } catch (err) {
        console.error("Error loading blog post:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center px-6">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-text-secondary text-sm">Loading article details...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex flex-col justify-center items-center px-6">
        <Icon name="AlertTriangle" size={48} className="text-warning mb-4" />
        <h1 className="text-2xl font-bold mb-2">Article Not Found</h1>
        <p className="text-text-secondary mb-6 text-center max-w-md">
          The blog post slug you are trying to read does not exist.
        </p>
        <Button variant="default" onClick={() => navigate('/blog')}>
          Back to Blog Log
        </Button>
      </div>
    );
  }

  // Handle Like Button click
  const handleLike = async () => {
    if (liked) return;
    try {
      const updatedLikes = (post.likes || 0) + 1;
      await dbUpdate('blogs', post.id, { likes: updatedLikes });
      setPost(prev => prev ? { ...prev, likes: updatedLikes } : null);
      setLiked(true);
    } catch (err) {
      console.error("Error liking post:", err);
    }
  };

  // Handle Comment Submission
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentName || !commentText) return;
    try {
      const newComment = {
        postSlug: slug,
        authorName: commentName,
        commentText: commentText,
        status: 'approved', // Auto-approve for demo
        createdAt: new Date().toISOString()
      };
      await dbInsert('blog_comments', newComment);
      setComments(prev => [...prev, newComment]);
      setCommentName('');
      setCommentText('');
      setCommentSubmitted(true);
      setTimeout(() => setCommentSubmitted(false), 5000);
    } catch (err) {
      console.error("Error submitting comment:", err);
    }
  };

  // Generate related posts (same category, excluding current post)
  const relatedPosts = blogs
    .filter(b => b.category === post.category && b.id !== post.id)
    .slice(0, 3);

  // --- Dynamic Table of Contents Extractor ---
  const extractHeadings = (markdownText) => {
    const lines = markdownText.split('\n');
    const headings = [];
    lines.forEach((line) => {
      const match = line.match(/^##\s+(.+)$/);
      if (match) {
        const title = match[1].trim();
        const anchor = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        headings.push({ title, anchor });
      }
    });
    return headings;
  };

  const headings = extractHeadings(post.content || '');

  // --- Custom Markdown-to-JSX Parser Helper ---
  const parseMarkdown = (markdownText) => {
    if (!markdownText) return null;
    const blocks = markdownText.split(/\n{2,}/);

    return blocks.map((block, idx) => {
      const trimmed = block.trim();
      if (!trimmed) return null;

      // H1 Header
      if (trimmed.startsWith('# ')) {
        return null; // Skip H1 inside body as it is in the Hero
      }

      // H2 Header (with anchors for Table of Contents)
      if (trimmed.startsWith('## ')) {
        const title = trimmed.replace(/^##\s+/, '').trim();
        const anchor = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return (
          <h2 key={idx} id={anchor} className="text-2xl md:text-3xl font-bold text-foreground mt-12 mb-6 scroll-mt-24">
            {title}
          </h2>
        );
      }

      // H3 Header
      if (trimmed.startsWith('### ')) {
        return (
          <h3 key={idx} className="text-xl font-bold text-foreground mt-8 mb-4">
            {trimmed.replace(/^###\s+/, '').trim()}
          </h3>
        );
      }

      // Quote Block
      if (trimmed.startsWith('> ')) {
        return (
          <blockquote key={idx} className="border-l-4 border-primary pl-6 my-6 italic text-text-secondary text-lg">
            {trimmed.replace(/^>\s+/, '').trim()}
          </blockquote>
        );
      }

      // Callout Box
      if (trimmed.startsWith('* ') && trimmed.includes('Takeaways')) {
        return (
          <div key={idx} className="p-6 bg-primary/10 border border-primary/25 rounded-2xl my-8">
            <h4 className="font-bold text-primary mb-3 flex items-center space-x-2">
              <Icon name="Lightbulb" size={18} />
              <span>Key Takeaways</span>
            </h4>
            <ul className="space-y-2 text-text-secondary text-sm md:text-base leading-relaxed pl-4 list-disc">
              {trimmed.split('\n').slice(1).map((line, lIdx) => (
                <li key={lIdx}>{line.replace(/^[\s*-]+/, '').trim()}</li>
              ))}
            </ul>
          </div>
        );
      }

      // Checklist Block
      if (trimmed.includes('[ ]') || trimmed.includes('[x]')) {
        return (
          <div key={idx} className="space-y-3 my-6">
            {trimmed.split('\n').map((line, lIdx) => {
              const checked = line.includes('[x]');
              return (
                <div key={lIdx} className="flex items-center space-x-3 text-sm md:text-base text-text-secondary">
                  <Icon name={checked ? 'CheckSquare' : 'Square'} size={18} className={checked ? 'text-primary' : 'text-slate-500'} />
                  <span>{line.replace(/^[\s*-\[\]x\s]+/, '').trim()}</span>
                </div>
              );
            })}
          </div>
        );
      }

      // Bulleted Lists
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        return (
          <ul key={idx} className="list-disc pl-6 space-y-2 text-text-secondary my-6 leading-relaxed text-sm md:text-base">
            {trimmed.split('\n').map((line, lIdx) => (
              <li key={lIdx}>
                {line.replace(/^[\s*-]+/, '').trim()}
              </li>
            ))}
          </ul>
        );
      }

      // Numbered Lists
      if (/^\d+\.\s+/.test(trimmed)) {
        return (
          <ol key={idx} className="list-decimal pl-6 space-y-2 text-text-secondary my-6 leading-relaxed text-sm md:text-base">
            {trimmed.split('\n').map((line, lIdx) => (
              <li key={lIdx}>
                {line.replace(/^\d+\.\s+/, '').trim()}
              </li>
            ))}
          </ol>
        );
      }

      // Comparison Table Support
      if (trimmed.startsWith('|')) {
        const rows = trimmed.split('\n');
        const headers = rows[0].split('|').map(x => x.trim()).filter(Boolean);
        const bodyRows = rows.slice(2).map(r => r.split('|').map(x => x.trim()).filter(Boolean));
        return (
          <div key={idx} className="overflow-x-auto my-8 border border-border rounded-2xl bg-card">
            <table className="w-full text-left text-sm md:text-base border-collapse">
              <thead>
                <tr className="bg-muted border-b border-border font-bold text-foreground">
                  {headers.map((h, hIdx) => (
                    <th key={hIdx} className="p-4 border-r border-border last:border-r-0">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((r, rIdx) => (
                  <tr key={rIdx} className="border-b border-border/50 last:border-b-0 text-text-secondary hover:bg-muted/10 transition-colors">
                    {r.map((col, cIdx) => (
                      <td key={cIdx} className="p-4 border-r border-border last:border-r-0 leading-relaxed">{col}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      // Normal Paragraph Text
      return (
        <p key={idx} className="text-text-secondary text-base md:text-lg leading-relaxed mb-6 font-normal">
          {trimmed.split('\n').map((line, lIdx) => {
            // Replace bold notation **text** with strong tag
            let parsedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            // Replace link notation [text](url) with a tags
            parsedLine = parsedLine.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>');
            return <span key={lIdx} dangerouslySetInnerHTML={{ __html: parsedLine }} />;
          })}
        </p>
      );
    });
  };

  // --- Dynamic JSON-LD Schemas ---
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": post.schemaType || "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "image": post.ogImage || "https://bytepairs.com/android-chrome-512x512.png",
    "datePublished": post.publishedDate,
    "dateModified": post.lastUpdated || post.publishedDate,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Byte Pairs",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bytepairs.com/android-chrome-512x512.png"
      }
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://bytepairs.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://bytepairs.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://bytepairs.com/blog/${post.slug}`
      }
    ]
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <Helmet>
        <title>{post.metaTitle || post.title}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={`https://bytepairs.com/blog/${post.slug}`} />

        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://bytepairs.com/blog/${post.slug}`} />
        <meta property="og:image" content={post.ogImage || "https://bytepairs.com/android-chrome-512x512.png"} />

        <meta name="twitter:card" content={post.twitterCard || "summary"} />
        <meta name="twitter:title" content={post.metaTitle || post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content={post.ogImage || "https://bytepairs.com/android-chrome-512x512.png"} />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Progress Reading Bar */}
      <div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-primary to-accent z-[9999] transition-all duration-100"
        style={{ width: `${scrollProgress}%` }}
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="pt-24 pb-20">

          {/* Article Hero */}
          <section className="py-12 bg-gradient-to-br from-primary/5 via-muted/20 to-accent/5 border-b border-border">
            <div className="max-w-4xl mx-auto px-6">

              {/* Back to Blog */}
              <Link to="/blog" className="inline-flex items-center space-x-1.5 text-xs text-text-secondary hover:text-primary transition-colors mb-6 font-semibold uppercase tracking-wider">
                <Icon name="ArrowLeft" size={14} />
                <span>Back to Engineering Log</span>
              </Link>

              {/* Tag / Category Indicator */}
              <div className="flex items-center space-x-3 mb-4">
                <span className="bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase">
                  {post.category}
                </span>
                <span className="text-text-secondary text-xs">{post.readingTime} min read</span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight text-foreground mb-6">
                {post.title}
              </h1>

              {/* Author & Meta */}
              <div className="flex items-center space-x-4 border-t border-border pt-6 mt-6">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary text-sm">
                  {post.author?.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-bold text-foreground">{post.author}</div>
                  <div className="text-xs text-text-secondary">
                    Published: {formatDate(post.publishedDate)} • Views: {post.views || 0}
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* Reading Layout Grid */}
          <section className="max-w-6xl mx-auto px-6 pt-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

              {/* Left Column: Sticky Table of Contents (Desktop only) */}
              <aside className="hidden lg:block lg:col-span-3">
                {headings.length > 0 && (
                  <div className="sticky top-28 space-y-4">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center space-x-1.5">
                      <Icon name="List" size={14} />
                      <span>Table of Contents</span>
                    </h4>
                    <nav className="flex flex-col space-y-2 border-l border-border pl-4">
                      {headings.map((h, hIdx) => (
                        <a
                          key={hIdx}
                          href={`#${h.anchor}`}
                          className="text-xs text-text-secondary hover:text-primary transition-colors leading-relaxed block"
                        >
                          {h.title}
                        </a>
                      ))}
                    </nav>
                  </div>
                )}
              </aside>

              {/* Center Column: Article content markup */}
              <article ref={articleRef} className="lg:col-span-6 font-sans antialiased">
                {post.ogImage && (
                  <div className="mb-8 rounded-3xl overflow-hidden border border-border bg-slate-950 aspect-video max-h-[360px] flex items-center justify-center p-2">
                    <img src={post.ogImage} alt={post.title} className="w-full h-full object-contain" />
                  </div>
                )}
                {/* Parse & render block items */}
                <div className="prose prose-invert max-w-none">
                  {parseMarkdown(post.content || '')}
                </div>

                {/* Engagement Interactions (Likes & Shares) */}
                <div className="flex flex-col sm:flex-row items-center justify-between py-8 border-y border-border my-12 gap-4">

                  {/* Like Button */}
                  <button
                    onClick={handleLike}
                    disabled={liked}
                    className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl border text-sm font-semibold transition-all ${liked ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border bg-card hover:bg-muted text-foreground'}`}
                  >
                    <Icon name="ThumbsUp" size={16} className={liked ? 'fill-current' : ''} />
                    <span>{liked ? 'Liked! Thank you' : `Like Article (${post.likes || 0})`}</span>
                  </button>

                  {/* Share buttons */}
                  <div className="flex items-center space-x-3 text-sm text-text-secondary">
                    <Icon name="Share2" size={16} />
                    <span>Share:</span>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors"
                    >
                      <Icon name="Linkedin" size={18} />
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors"
                    >
                      <Icon name="Twitter" size={18} />
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert("Article URL copied to clipboard!");
                      }}
                      className="hover:text-primary transition-colors"
                    >
                      <Icon name="Link" size={18} />
                    </button>
                  </div>

                </div>

                {/* Interactive Comments Form */}
                <div className="my-12">
                  <h3 className="text-xl font-bold text-foreground mb-6 flex items-center space-x-2">
                    <Icon name="MessageSquare" size={20} className="text-primary" />
                    <span>Comments ({comments.length})</span>
                  </h3>

                  {comments.length === 0 ? (
                    <p className="text-sm text-text-secondary italic mb-8">No comments yet. Be the first to share your thoughts!</p>
                  ) : (
                    <div className="space-y-4 mb-8">
                      {comments.map((c, idx) => (
                        <div key={idx} className="p-5 bg-card border border-border rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-sm text-foreground">{c.authorName}</span>
                            <span className="text-[10px] text-text-secondary">{formatDate(c.createdAt)}</span>
                          </div>
                          <p className="text-text-secondary text-sm leading-relaxed">{c.commentText}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <form onSubmit={handleCommentSubmit} className="p-6 bg-card border border-border rounded-2xl space-y-4">
                    <h4 className="font-semibold text-sm text-foreground">Post a Comment</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <input
                        type="text"
                        required
                        placeholder="Your Name"
                        value={commentName}
                        onChange={(e) => setCommentName(e.target.value)}
                        className="w-full px-4 py-2.5 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                      />
                      <textarea
                        required
                        rows={4}
                        placeholder="Type your message here..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        className="w-full px-4 py-2.5 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                      />
                    </div>
                    <button
                      type="submit"
                      className="bg-primary hover:bg-accent text-white px-5 py-2 rounded-xl text-xs font-semibold hover:shadow-brand-sm transition-all"
                    >
                      {commentSubmitted ? 'Comment Logged! ✅' : 'Submit Comment'}
                    </button>
                  </form>
                </div>

              </article>

              {/* Right Column: Related Articles Widget */}
              <aside className="lg:col-span-3 space-y-8">
                {relatedPosts.length > 0 && (
                  <div className="sticky top-28 space-y-6">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center space-x-1.5 border-b border-border pb-2">
                      <Icon name="Bookmark" size={14} className="text-primary" />
                      <span>Related Reads</span>
                    </h4>
                    <div className="space-y-6">
                      {relatedPosts.map((post) => (
                        <div key={post.id} className="group text-sm">
                          <Link to={`/blog/category/${post.category?.toLowerCase()}`} className="text-primary text-[10px] font-bold uppercase tracking-wider block mb-1">
                            {post.category}
                          </Link>
                          <h5 className="font-bold text-foreground group-hover:text-primary transition-colors leading-snug line-clamp-2">
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </h5>
                          <span className="text-[10px] text-text-secondary block mt-1">{post.readingTime} min read</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </aside>

            </div>
          </section>

          {/* Newsletter Section */}
          <section className="py-16 bg-muted/20 border-t border-border mt-16">
            <div className="max-w-3xl mx-auto px-6 text-center">
              <div className="w-12 h-12 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name="MailOpen" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Join our Engineering Digest</h3>
              <p className="text-text-secondary text-sm max-w-md mx-auto mb-6">
                Direct updates from our full-stack engineering logs. No fluff. Factual code guides only.
              </p>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  alert("Successfully signed up!");
                }}
                className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto"
              >
                <input
                  type="email"
                  required
                  placeholder="yourname@domain.com"
                  className="px-4 py-2.5 bg-card border border-border rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary flex-grow text-center sm:text-left"
                />
                <button type="submit" className="bg-primary hover:bg-accent text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:shadow-brand-sm transition-all duration-300">
                  Subscribe
                </button>
              </form>
            </div>
          </section>

        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPostPage;
